import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  GetApplyHistoryDetailRequest,
  GetApplyHistoryDetailResponse,
  GetApplyHistoryRequest,
  GetApplyHistoryResponse,
  GetApplyNoInfoCountResponse,
  GetDailyAttendanceStatusResponse,
  GetMyPointResponse,
  GetPointHistoryRequest,
  GetPointHistoryResponse,
  PostDailyAttendanceResponse,
  UpdateApplyInfoRequest,
  UpdateApplyInfoResponse,
} from 'src/store/server/point/point.dto';
import { pointService } from 'src/store/server/point/point.service';
import type { APICommonResponse } from 'src/types/common.types';
import { checkLogin } from 'src/utils/auth';

export const USE_POINT_QUERY_KEY = {
  getMyPoint: 'get-my-point',
  getPointHistory: 'get-point-history',
  getApplyNoInfoCount: 'get-apply-no-info-count',
  getApplyHistory: 'get-apply-history',
  getApplyHistoryDetail: 'get-apply-history-detail',
  getDailyAttendanceStatus: 'get-daily-attendance-status',
};

// 보유 코튼(포인트) 조회
export const useGetMyPointQuery = () => {
  return useQuery([USE_POINT_QUERY_KEY.getMyPoint], () => pointService.getMyPoint(), {
    select: (res: APICommonResponse<GetMyPointResponse>) => {
      const { data } = res.data;
      return {
        point: data.point,
        expiredDttm: data.expired_dttm,
        expiredPoint: data.expired_point,
      };
    },
    enabled: checkLogin(),
  });
};

// 코튼(포인트) 히스토리 조회
export const useGetPointHistoryQuery = (params: GetPointHistoryRequest) => {
  return useQuery([USE_POINT_QUERY_KEY.getPointHistory, params], () => pointService.getPointHistory(params), {
    select: (res: APICommonResponse<GetPointHistoryResponse>) => {
      const { data } = res.data;
      return {
        count: data.count,
        rows: data.rows.map((item) => ({
          pointIdx: item.point_idx,
          title: item.title,
          methodCode: item.method_code,
          type: item.type,
          point: item.point,
          regDttm: item.reg_dttm,
        })),
      };
    },
  });
};

// 응모자 정보 미입력 조회
export const useGetApplyNoInfoCountQuery = () => {
  return useQuery([USE_POINT_QUERY_KEY.getApplyNoInfoCount], () => pointService.getApplyNoInfoCount(), {
    select: (res: APICommonResponse<GetApplyNoInfoCountResponse>) => {
      const { data } = res.data;
      return {
        count: data.count,
      };
    },
  });
};

// 응모내역 조회
export const useGetApplyHistoryQuery = (params: GetApplyHistoryRequest) => {
  return useQuery([USE_POINT_QUERY_KEY.getApplyHistory, params], () => pointService.getApplyHistory(params), {
    select: (res: APICommonResponse<GetApplyHistoryResponse>) => {
      const { data } = res.data;
      return {
        count: data.count,
        noDataCount: data.no_data_count,
        rows: data.rows.map((item) => ({
          histIdx: item.hist_idx,
          methodTitle: item.method_title,
          productTitle: item.product_title,
          status: item.status,
          applyType: item.apply_type,
          endDttm: item.end_dttm,
          regDttm: item.reg_dttm,
          infoYn: item.info_yn,
        })),
      };
    },
  });
};

// 응모내역 상세 조회
interface GetApplyHistoryDetailParams {
  histIdx: GetApplyHistoryDetailRequest['hist_idx'];
}

export const useGetApplyHistoryDetailQuery = ({ histIdx }: GetApplyHistoryDetailParams) => {
  return useQuery(
    [USE_POINT_QUERY_KEY.getApplyHistoryDetail, histIdx],
    () => pointService.getApplyHistoryDetail({ hist_idx: histIdx }),
    {
      select: (res: APICommonResponse<GetApplyHistoryDetailResponse>) => {
        const { data } = res.data;
        return {
          histIdx: data.hist_idx,
          applyType: data.apply_type,
          name: data.name,
          phone: data.phone,
          birth: data.birth,
          postNo: data.post_no,
          address: data.address,
          addressDetail: data.address_detail,
          idCard: data.id_card,
          privacyUrl: data.privacy_url,
          thirdPartyUrl: data.third_party_url,
          regDttm: data.reg_dttm,
          collectionInfo: data.collection_info,
        };
      },
      enabled: !!histIdx,
    },
  );
};

// 포인트 응모 정보 입력 (Update)
interface UpdateApplyInfoParams {
  histIdx: UpdateApplyInfoRequest['hist_idx'];
  name: UpdateApplyInfoRequest['name'];
  phone: UpdateApplyInfoRequest['phone'];
  birth?: UpdateApplyInfoRequest['birth'];
  postNo?: UpdateApplyInfoRequest['post_no'];
  address?: UpdateApplyInfoRequest['address'];
  addressDetail?: UpdateApplyInfoRequest['address_detail'];
  idCardFile?: UpdateApplyInfoRequest['id_card_file'];
}

export const useUpdateApplyInfoMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: APICommonResponse<UpdateApplyInfoResponse>) => void;
  onError?: (e) => void;
} = {}) => {
  return useMutation(
    (params: UpdateApplyInfoParams) =>
      pointService.updateApplyInfo({
        hist_idx: params.histIdx,
        name: params.name,
        phone: params.phone,
        birth: params.birth,
        post_no: params.postNo,
        address: params.address,
        address_detail: params.addressDetail,
        id_card_file: params.idCardFile,
      }),
    {
      onSuccess,
      onError,
    },
  );
};

// 데일리 코튼(포인트) 참여 여부 조회
export const useGetDailyAttendanceStatusQuery = () => {
  return useQuery([USE_POINT_QUERY_KEY.getDailyAttendanceStatus], () => pointService.getDailyAttendanceStatus(), {
    select: (res: APICommonResponse<GetDailyAttendanceStatusResponse>) => {
      const { data } = res.data;
      return {
        regDttm: data.reg_dttm,
        period: data.period,
      };
    },
    enabled: checkLogin(),
  });
};

// 데일리 코튼(포인트) 참여
export const usePostDailyAttendanceMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: APICommonResponse<PostDailyAttendanceResponse>) => void;
  onError?: (e) => void;
} = {}) => {
  return useMutation(() => pointService.postDailyAttendance(), {
    onSuccess,
    onError,
  });
};
